import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import {
  Button,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Input,
  Label,
  Accordion,
  Select,
  SelectItem,
  DateInput,
} from '../../components/ui';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../../components/ui/Table';
import {
  BuildingIcon,
  ClipboardIcon,
  DocumentTextIcon,
  PlusIcon,
  ClipboardCheckIcon,
} from '../../components/ui/Icons';
import {
  fetchTicketingDepartment,
  fetchMyTicketingRequests,
  fetchTicketingDepartmentUsers,
} from './ticketingApi';
import { TICKETING_DATA_CHANGED_EVENT } from './ticketingStorage';
import { TicketingConversationModal } from './components/TicketingConversationModal';
import { getTicketingPayloadSummary } from './ticketingPayloadSummary';
import { useAuth } from '../../contexts/AuthContext';
import { canViewRouteForUser, hrefForTicketingDepartment } from '../../admin/permissions/permissionStore';

const MY_TICKET_STATUS_TABS = [
  { value: 'ALL', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

function statusLabel(s) {
  const m = {
    OPEN: 'Open',
    IN_PROGRESS: 'In progress',
    RESOLVED: 'Resolved',
    CANCELLED: 'Cancelled',
  };
  return m[s] || s || '—';
}

function lastMessageAuthorName(msg) {
  const a = msg?.author;
  if (!a) return '—';
  return a.userDisplayName || a.userEmail || (a.id != null ? `User #${a.id}` : '—');
}

function ticketSubjectText(ticket) {
  const topLevel = typeof ticket?.subject === 'string' ? ticket.subject.trim() : '';
  if (topLevel) return topLevel;
  const payloadSubject =
    ticket?.payload && typeof ticket.payload === 'object' && !Array.isArray(ticket.payload)
      ? ticket.payload.subject
      : '';
  return typeof payloadSubject === 'string' && payloadSubject.trim()
    ? payloadSubject.trim()
    : '—';
}

function toDateValue(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function createdByLabel(u) {
  if (!u) return 'Unknown';
  return (
    u.userDisplayName ||
    [u.userFirstName, u.userLastName].filter(Boolean).join(' ').trim() ||
    u.userEmail ||
    (u.id != null ? `User #${u.id}` : 'Unknown')
  );
}

const TicketingDepartmentPage = () => {
  const { departmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const [department, setDepartment] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [sectionTab, setSectionTab] = useState('types');
  const [statusTab, setStatusTab] = useState('ALL');
  const [myTickets, setMyTickets] = useState([]);
  const [myLoading, setMyLoading] = useState(false);
  const [myError, setMyError] = useState(null);
  const [conversationTicket, setConversationTicket] = useState(null);
  const [myTicketsSearch, setMyTicketsSearch] = useState('');
  const [createdDate, setCreatedDate] = useState('');
  const [createdByUserId, setCreatedByUserId] = useState('');
  const [departmentUsers, setDepartmentUsers] = useState([]);

  const departmentHref = useMemo(
    () => (departmentId != null && departmentId !== '' ? hrefForTicketingDepartment(departmentId) : null),
    [departmentId]
  );
  const mayViewDepartment = useMemo(
    () => !departmentHref || canViewRouteForUser(user, departmentHref),
    [departmentHref, user]
  );

  const statusForApi = statusTab === 'ALL' ? '' : statusTab;
  const groupedCategories = useMemo(() => {
    const buckets = new Map();
    categories.forEach((cat) => {
      const key = String(cat.categoryGroup || '').trim() || 'Ungrouped';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(cat);
    });
    return Array.from(buckets.entries()).map(([group, items]) => ({
      group,
      items,
    }));
  }, [categories]);

  const load = useCallback(async () => {
    if (departmentId == null || departmentId === '') {
      setDepartment(null);
      setCategories([]);
      setLoading(false);
      return;
    }
    const href = hrefForTicketingDepartment(departmentId);
    if (href && !canViewRouteForUser(user, href)) {
      setDepartment(null);
      setCategories([]);
      setLoadError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const dep = await fetchTicketingDepartment(departmentId);
      setDepartment(dep);
      setCategories(dep.categories || []);
    } catch (e) {
      setLoadError(e?.message || 'Failed to load department');
      setDepartment(null);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [departmentId, user]);

  const loadMyTickets = useCallback(async () => {
    if (departmentId == null || departmentId === '') return;
    const href = hrefForTicketingDepartment(departmentId);
    if (href && !canViewRouteForUser(user, href)) return;
    setMyLoading(true);
    setMyError(null);
    try {
      const list = await fetchMyTicketingRequests({
        departmentId,
        ...(statusForApi ? { status: statusForApi } : {}),
        ...(myTicketsSearch.trim() ? { search: myTicketsSearch.trim() } : {}),
      });
      setMyTickets(Array.isArray(list) ? list : []);
    } catch (e) {
      setMyError(e?.message || 'Failed to load your tickets');
      setMyTickets([]);
    } finally {
      setMyLoading(false);
    }
  }, [departmentId, statusForApi, myTicketsSearch, user]);

  const loadDepartmentUsers = useCallback(async () => {
    if (departmentId == null || departmentId === '') {
      setDepartmentUsers([]);
      return;
    }
    const href = hrefForTicketingDepartment(departmentId);
    if (href && !canViewRouteForUser(user, href)) {
      setDepartmentUsers([]);
      return;
    }
    try {
      const usersInDepartment = await fetchTicketingDepartmentUsers({
        linkedUserDepartmentId: department?.userDepartmentId,
      });
      setDepartmentUsers(Array.isArray(usersInDepartment) ? usersInDepartment : []);
    } catch {
      setDepartmentUsers([]);
    }
  }, [departmentId, department?.userDepartmentId, user]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (sectionTab !== 'my-tickets') return;
    loadMyTickets();
  }, [sectionTab, loadMyTickets]);

  useEffect(() => {
    const refresh = () => {
      load();
      if (sectionTab === 'my-tickets') {
        loadMyTickets();
      }
    };
    window.addEventListener(TICKETING_DATA_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(TICKETING_DATA_CHANGED_EVENT, refresh);
  }, [load, loadMyTickets, sectionTab]);

  useEffect(() => {
    loadDepartmentUsers();
  }, [loadDepartmentUsers]);

  const filteredMyTickets = useMemo(() => {
    return myTickets.filter((t) => {
      if (createdDate) {
        const ticketCreatedDate = toDateValue(t.createdAt);
        if (ticketCreatedDate !== createdDate) return false;
      }
      if (createdByUserId) {
        if (String(t.ticketRequestedFromUserId ?? '') !== createdByUserId) return false;
      }
      return true;
    });
  }, [myTickets, createdDate, createdByUserId]);

  if (departmentHref && !mayViewDepartment) {
    return (
      <div
        className={cn(
          'min-h-[40vh] flex flex-col items-center justify-center p-8 text-center max-w-md mx-auto',
          theme.text.primary
        )}
      >
        <div
          className={cn(
            'rounded-2xl p-6 mb-4 border',
            theme.border.light,
            theme.colors.surface,
            theme.shadow.default
          )}
        >
          <BuildingIcon className={cn('h-12 w-12 mx-auto mb-3 opacity-40', theme.text.muted)} />
          <h1 className="text-lg font-semibold mb-1">Access denied</h1>
          <p className={cn('text-sm', theme.text.muted)}>
            You do not have permission to view this ticketing department.
          </p>
          <Button type="button" className="mt-4" variant="outline" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={cn('p-8 text-center', theme.text.muted)}>Loading department…</div>
    );
  }

  if (loadError || !department) {
    return (
      <div
        className={cn(
          'min-h-[40vh] flex flex-col items-center justify-center p-8 text-center max-w-md mx-auto',
          theme.text.primary
        )}
      >
        <div
          className={cn(
            'rounded-2xl p-6 mb-4 border',
            theme.border.light,
            theme.colors.surface,
            theme.shadow.default
          )}
        >
          <BuildingIcon className={cn('h-12 w-12 mx-auto mb-3 opacity-40', theme.text.muted)} />
          <h1 className="text-lg font-semibold mb-1">Department not found</h1>
          <p className={cn('text-sm', theme.text.muted)}>
            {loadError || 'This ticketing department does not exist or was removed.'}
          </p>
        </div>
        <Button type="button" variant="primary" onClick={() => navigate('/ticketing/options')}>
          Open ticketing setup
        </Button>
      </div>
    );
  }

  const requestTypesSection = (
    <>
      <div className="mb-5">
        <h2 className={cn('text-sm font-semibold uppercase tracking-wider', theme.text.muted)}>
          Request types
        </h2>
        <p className={cn('text-sm mt-1', theme.text.muted)}>Click a card to open the request form.</p>
      </div>

      {categories.length === 0 ? (
        <div
          className={cn(
            'rounded-2xl border-2 border-dashed px-8 py-14 text-center',
            theme.border.light,
            theme.colors.surface
          )}
        >
          <ClipboardIcon className={cn('h-14 w-14 mx-auto mb-4 opacity-30', theme.text.muted)} />
          <p className={cn('text-base font-medium', theme.text.primary)}>No request types yet</p>
          <p className={cn('text-sm mt-1 max-w-sm mx-auto', theme.text.muted)}>
            Add types in ticketing setup to see them listed here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groupedCategories.map((groupBlock) => (
            <Accordion
              key={groupBlock.group}
              title={groupBlock.group}
              subtitle={`${groupBlock.items.length} request type${groupBlock.items.length !== 1 ? 's' : ''}`}
              defaultOpen
            >
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5 list-none p-0 m-0">
                {groupBlock.items.map((cat, index) => (
                  <li key={cat.id}>
                    <Link
                      to={`/ticketing/request/${encodeURIComponent(department.id)}/${encodeURIComponent(cat.id)}`}
                      className={cn(
                        'block no-underline text-inherit',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded-2xl'
                      )}
                    >
                      <article
                        className={cn(
                          'group relative h-full overflow-hidden rounded-2xl border transition-all duration-300',
                          theme.border.light,
                          theme.colors.surface,
                          'hover:shadow-xl hover:shadow-blue-500/10 hover:-translate-y-1',
                          'dark:hover:shadow-indigo-900/30'
                        )}
                      >
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-indigo-600 opacity-90" />
                        <div className="pl-5 pr-5 pt-5 pb-5 md:pl-6">
                          <div className="flex items-start justify-between gap-3">
                            <div
                              className={cn(
                                'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl',
                                'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
                                'ring-1 ring-blue-100 dark:ring-blue-900/50'
                              )}
                            >
                              <DocumentTextIcon className="h-5 w-5" />
                            </div>
                            {cat.record_order != null ? (
                              <span
                                className={cn(
                                  'flex-shrink-0 text-[11px] font-semibold tabular-nums uppercase tracking-wide',
                                  'px-2 py-1 rounded-md',
                                  'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                                )}
                              >
                                #{cat.record_order}
                              </span>
                            ) : (
                              <span
                                className={cn(
                                  'flex-shrink-0 text-[11px] font-medium',
                                  'px-2 py-1 rounded-md opacity-70',
                                  'bg-slate-50 text-slate-500 dark:bg-slate-900/50 dark:text-slate-400'
                                )}
                              >
                                · {index + 1}
                              </span>
                            )}
                          </div>
                          <h3
                            className={cn(
                              'mt-4 text-lg font-semibold leading-snug',
                              theme.text.primary,
                              'group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors'
                            )}
                          >
                            {cat.name}
                          </h3>
                          {cat.description ? (
                            <div
                              className={cn(
                                'mt-3 rounded-xl px-3.5 py-3 text-sm leading-relaxed',
                                'bg-slate-50/90 dark:bg-slate-900/40',
                                theme.text.secondary,
                                'border border-slate-100/80 dark:border-slate-800'
                              )}
                            >
                              <p className="whitespace-pre-wrap line-clamp-[8]">{cat.description}</p>
                            </div>
                          ) : (
                            <p className={cn('mt-3 text-sm italic', theme.text.muted)}>No description</p>
                          )}
                        </div>
                      </article>
                    </Link>
                  </li>
                ))}
              </ul>
            </Accordion>
          ))}
        </div>
      )}
    </>
  );

  const myTicketsSection = (
    <div className="space-y-4">
      <p className={cn('text-sm', theme.text.muted)}>
        Tickets you submitted in <span className="font-medium text-slate-700 dark:text-slate-200">{department.name}</span>
        . Open comments to read the full history or send a message anytime.
      </p>

      <Tabs value={statusTab} onValueChange={setStatusTab}>
        <TabsList
          className={cn(
            'flex flex-wrap gap-1 rounded-xl p-1 border',
            'bg-slate-100/70 dark:bg-slate-900/40',
            theme.border.light
          )}
        >
          {MY_TICKET_STATUS_TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className={cn(
                'rounded-lg px-3 py-1.5 text-xs sm:text-sm font-medium transition-all duration-300',
                statusTab === t.value
                  ? (theme.sidebar?.selected ||
                      `${theme.colors.primary} ${theme.text.inverse} ${theme.shadow.lg}`)
                  : `${theme.text.secondary} border-transparent bg-transparent hover:${theme.colors.background} hover:${theme.text.primary} hover:${theme.shadow.default}`
              )}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mt-3 flex flex-col lg:flex-row gap-3 lg:items-end">
        <div className="w-full lg:flex-1 max-w-md">
          <Label htmlFor="my-tickets-search" className="mb-1.5 block text-xs">
            Search tickets
          </Label>
          <Input
            id="my-tickets-search"
            value={myTicketsSearch}
            onChange={(e) => setMyTicketsSearch(e.target.value)}
            placeholder="Ticket #, payload keywords…"
            className="w-full"
          />
        </div>
        <div className="w-full lg:w-auto lg:min-w-[180px]">
          <Label htmlFor="department-ticket-created-date" className="mb-1.5 block text-xs">
            Date Created
          </Label>
          <DateInput
            id="department-ticket-created-date"
            name="createdDate"
            value={createdDate}
            onChange={(e) => setCreatedDate(e.target.value)}
            showClearButton={false}
            showFormatHint={false}
            className="w-full"
          />
        </div>
        <div className="w-full lg:w-auto lg:min-w-[240px]">
          <Label htmlFor="department-ticket-created-by" className="mb-1.5 block text-xs">
            Created By
          </Label>
          <Select
            id="department-ticket-created-by"
            name="createdBy"
            value={createdByUserId}
            onChange={(e) => setCreatedByUserId(e.target.value)}
            showClearButton={false}
            className="w-full"
          >
            <SelectItem value="">All users</SelectItem>
            {departmentUsers.map((u) => (
              <SelectItem key={u.id} value={String(u.id)}>
                {createdByLabel(u)}
              </SelectItem>
            ))}
          </Select>
        </div>
      </div>

      <div className="mt-2">
        {myError ? (
          <p className="text-sm text-red-600 dark:text-red-400">{myError}</p>
        ) : null}
        {myLoading ? (
          <p className={cn('text-sm py-8 text-center', theme.text.muted)}>Loading your tickets…</p>
        ) : filteredMyTickets.length === 0 ? (
          <p className={cn('text-sm py-8 text-center', theme.text.muted)}>No tickets in this status.</p>
        ) : (
          <div className={cn('w-full overflow-x-auto rounded-xl border', theme.border.light)}>
            <Table className="w-full min-w-[960px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20 whitespace-nowrap">ID</TableHead>
                  <TableHead className="min-w-[180px] max-w-[260px]">Subject</TableHead>
                  <TableHead className="w-48 min-w-[10rem]">Type</TableHead>
                  <TableHead className="min-w-[12rem]">Request</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="min-w-[200px] max-w-[280px]">Latest message</TableHead>
                  <TableHead className="text-right">Comments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMyTickets.map((t) => {
                  const { preview } = getTicketingPayloadSummary(t.formKey, t.payload);
                  const subjectText = ticketSubjectText(t);
                  const openComments = () =>
                    setConversationTicket({
                      id: t.id,
                      ticketRequestedFromUserId: t.ticketRequestedFromUserId,
                    });
                  return (
                    <TableRow key={t.id}>
                      <TableCell className="font-mono font-medium">
                        <Link
                          to={`/ticketing/ticket/${t.id}`}
                          state={{ from: location }}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          #{t.id}
                        </Link>
                      </TableCell>
                      <TableCell className="max-w-[260px] truncate" title={subjectText}>
                        {subjectText}
                      </TableCell>
                      <TableCell className="max-w-[160px] truncate" title={t.ticketingCategory?.name}>
                        {t.ticketingCategory?.name || '—'}
                      </TableCell>
                      <TableCell>
                        <p
                          className={cn(
                            'text-xs leading-snug whitespace-pre-wrap break-words m-0',
                            theme.text.secondary,
                          )}
                          title={preview}
                        >
                          {preview}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-normal">
                          {statusLabel(t.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {formatDateTime(t.createdAt)}
                      </TableCell>
                      <TableCell className="align-top max-w-[280px]">
                        {t.lastMessage ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className={cn(
                              'w-full h-auto text-left rounded-lg border border-transparent px-2 py-1.5 -mx-2 -my-1 transition-colors justify-start',
                              'hover:bg-slate-100/90 dark:hover:bg-slate-800/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40',
                            )}
                            onClick={openComments}
                            title="Open full message history"
                          >
                            <p
                              className={cn('text-xs leading-snug line-clamp-2 m-0', theme.text.primary)}
                            >
                              {t.lastMessage.body}
                            </p>
                            <p className={cn('text-[10px] mt-1 m-0', theme.text.muted)}>
                              {lastMessageAuthorName(t.lastMessage)} ·{' '}
                              {formatDateTime(t.lastMessage.createdAt)}
                            </p>
                            {(t.messageCount ?? 0) > 1 ? (
                              <p className="text-[10px] font-medium text-blue-600 dark:text-blue-400 m-0 mt-1">
                                View all {t.messageCount} messages →
                              </p>
                            ) : null}
                          </Button>
                        ) : (
                          <p className={cn('text-xs m-0', theme.text.muted)}>No messages yet</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={openComments}
                          title="Open full message history"
                        >
                          {(t.messageCount ?? 0) > 0 ? `Comments (${t.messageCount})` : 'Comments'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={cn('min-h-full', theme.gradients?.background || '')}>
      <div className="w-full px-3 py-4 md:px-4 md:py-5">
        <header
          className={cn(
            'relative w-full overflow-hidden rounded-2xl border mb-8',
            theme.border.light,
            theme.colors.surface,
            theme.shadow.lg
          )}
        >
          <div className="h-1.5 w-full bg-gradient-to-r from-blue-600 via-indigo-500 to-sky-500" />
          <div className="p-4 md:p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex gap-3 min-w-0 items-center">
                <div
                  className={cn(
                    'flex-shrink-0 h-11 w-11 rounded-xl flex items-center justify-center',
                    'bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-600/20'
                  )}
                >
                  <BuildingIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h1 className={cn('text-xl md:text-2xl font-bold tracking-tight', theme.text.primary)}>
                    {department.name}
                  </h1>
                  <p className={cn('text-xs md:text-sm mt-0.5', theme.text.muted)}>
                    Select a request type or open My tickets.
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <span className="inline-flex items-center rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 px-3 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200">
                  {categories.length} {categories.length === 1 ? 'type' : 'types'}
                </span>
              </div>
            </div>
            {department.description ? (
              <p className={cn('text-sm mt-3 leading-relaxed', theme.text.secondary)}>
                {department.description}
              </p>
            ) : null}
          </div>
        </header>

        <Tabs value={sectionTab} onValueChange={setSectionTab} className="w-full">
          <div className="mb-6 max-w-[520px]">
            <TabsList
              className={cn(
                'w-full grid grid-cols-2 gap-1 rounded-xl p-1 border',
                'bg-slate-100/70 dark:bg-slate-900/40',
                theme.border.light
              )}
            >
              <TabsTrigger
                value="types"
                className={cn(
                  'w-full rounded-lg border px-3 py-2 text-sm font-medium text-left transition-all duration-300',
                  sectionTab === 'types'
                    ? (theme.sidebar?.selected ||
                        `${theme.colors.primary} ${theme.text.inverse} ${theme.shadow.lg}`)
                    : `${theme.text.secondary} border-transparent bg-transparent hover:${theme.colors.background} hover:${theme.text.primary} hover:${theme.shadow.default}`
                )}
              >
                <span className="inline-flex items-center gap-2">
                  <PlusIcon className="h-4 w-4" />
                  <span>New request</span>
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="my-tickets"
                className={cn(
                  'w-full rounded-lg border px-3 py-2 text-sm font-medium text-left transition-all duration-300',
                  sectionTab === 'my-tickets'
                    ? (theme.sidebar?.selected ||
                        `${theme.colors.primary} ${theme.text.inverse} ${theme.shadow.lg}`)
                    : `${theme.text.secondary} border-transparent bg-transparent hover:${theme.colors.background} hover:${theme.text.primary} hover:${theme.shadow.default}`
                )}
              >
                <span className="inline-flex items-center gap-2">
                  <ClipboardCheckIcon className="h-4 w-4" />
                  <span>My tickets</span>
                </span>
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="types" className="w-full max-w-none">
            {requestTypesSection}
          </TabsContent>
          <TabsContent value="my-tickets" className="w-full max-w-none">
            {!user?.id ? (
              <div
                className={cn(
                  'rounded-xl border px-4 py-6 text-center text-sm max-w-6xl mx-auto',
                  theme.border.light,
                  theme.colors.surface,
                  theme.text.muted,
                )}
              >
                Sign in to see tickets you submitted for this department.
              </div>
            ) : (
              myTicketsSection
            )}
          </TabsContent>
        </Tabs>

        <TicketingConversationModal
          isOpen={conversationTicket != null}
          onClose={() => setConversationTicket(null)}
          ticket={conversationTicket}
        />
      </div>
    </div>
  );
};

export default TicketingDepartmentPage;
